import type { PlayerVisitRes, PlayerVisitsRes } from '@on3/api';
import { ConditionalButton } from '@on3/ui-lib/components/Button/Conditional';
import { SortableHeader } from '@on3/ui-lib/components/Table/SortableHeader';
import clsx from 'clsx';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useCallback, useState } from 'react';

import styles from './VisitCenterItems.module.scss';

export const VisitCenterItems = ({
  organizationCounts,
  showAll,
  visits,
}: Omit<PlayerVisitsRes, 'counts'> & {
  showAll?: boolean;
}) => {
  const organizations = showAll
    ? organizationCounts
    : organizationCounts?.slice(0, 3);

  visits = showAll ? visits : visits?.slice(0, 5);

  const [activeOrg, setActiveOrg] = useState<number | null>(null);
  const [sortOpts, setSortOpts] = useState<{
    key: keyof PlayerVisitRes;
    dir: 'asc' | 'desc';
  }>({
    key: 'dateOccurred',
    dir: 'desc',
  });
  const [playerVisits, setPlayerVisits] = useState<PlayerVisitRes[]>(visits);

  const handleFilter = useCallback(
    (orgKey: number) =>
      showAll
        ? () => {
            if (orgKey === activeOrg) {
              setActiveOrg(null);
              setPlayerVisits(visits);
            } else {
              setActiveOrg(orgKey);
              setPlayerVisits(
                visits?.filter((visit) => visit.organization.key === orgKey),
              );
            }
          }
        : undefined,
    [showAll, activeOrg, visits],
  );

  const handleSort = useCallback(
    (key: keyof PlayerVisitRes) =>
      showAll
        ? () => {
            const dir =
              key === sortOpts.key
                ? sortOpts.dir === 'asc'
                  ? 'desc'
                  : 'asc'
                : 'desc';

            const sorted = [...visits].sort((a, b) => {
              const aKey = (a[key] as number) ?? 0;
              const bKey = (b[key] as number) ?? 0;

              return dir === 'asc' ? aKey - bKey : bKey - aKey;
            });

            setPlayerVisits(sorted);
            setSortOpts({
              key,
              dir,
            });
          }
        : undefined,
    [showAll, visits, sortOpts],
  );

  return (
    <div
      className={clsx(styles.block, {
        [styles.module]: !showAll,
      })}
    >
      <div className={styles.tiles}>
        {organizations?.map(({ organization, total }, index) => (
          <ConditionalButton
            className={clsx(styles.tile, {
              [styles.active]: activeOrg === organization.key,
            })}
            key={index}
            onClick={handleFilter(organization.key)}
          >
            <TeamAvatar
              height={32}
              src={organization.assetUrl}
              teamName={organization.name}
              width={32}
            />
            <span>{total}</span>
          </ConditionalButton>
        ))}
      </div>
      <table>
        <thead>
          <tr>
            <th>School</th>
            <SortableHeader
              active={sortOpts.key === 'dateOccurred'}
              onSort={handleSort('dateOccurred')}
              sortDir={sortOpts.dir}
            >
              Date
            </SortableHeader>
            {showAll && (
              <SortableHeader
                active={sortOpts.key === 'distance'}
                onSort={handleSort('distance')}
                sortDir={sortOpts.dir}
              >
                Distance
              </SortableHeader>
            )}
            <th>Visit Type</th>
          </tr>
        </thead>
        <tbody>
          {playerVisits?.map((visit, index) => {
            const utcDate = utcToZonedTime(visit.dateOccurred * 1000, 'UTC');

            return (
              <tr key={index}>
                <td>
                  <div className={styles.team}>
                    <TeamAvatar
                      height={32}
                      src={visit.organization.assetUrl}
                      teamName={visit.organization.name}
                      width={32}
                    />
                    <div data-abbr={visit.organization.abbreviation}>
                      <span>{visit.organization.name}</span>
                    </div>
                  </div>
                </td>
                <td className={styles.date}>{format(utcDate, 'M/d/yy')}</td>
                {showAll && (
                  <td>
                    {visit.distance ? `${visit.distance.toFixed(0)} mi.` : '-'}
                  </td>
                )}
                <td className={styles.type}>
                  <span data-type={visit.type?.toLowerCase()}>
                    {visit.type}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
